/* kware start editing
 * AutomaticFeeFineSettings.js
 * REASON: FOLIO does not provide a UI to manage notice templates for automatic fee/fine types
 * (Overdue fine, Lost item fee, etc.). This page allows linking charge/action notice templates
 * to automatic fee/fine types only, without allowing any other modifications.
 * This is required so that PatronNoticeService can find the actionNoticeId/chargeNoticeId
 * when sending notices for automated fee/fine events (payment, waiver, etc.).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Field } from 'react-final-form';
import { Select, NoValue } from '@folio/stripes/components';
import { ControlledVocab } from '@folio/stripes/smart-components';
import { stripesConnect, stripesShape, withStripes } from '@folio/stripes/core';

import { MAX_RECORDS } from '../constants';

const columnMapping = {
  feeFineType: <FormattedMessage id="ui-users.feefines.columns.type" />,
  chargeNoticeId: <FormattedMessage id="ui-users.feefines.columns.chargeNotice" />,
  actionNoticeId: <FormattedMessage id="ui-users.feefines.columns.actionNotice" />,
};

class AutomaticFeeFineSettings extends React.Component {
  static manifest = Object.freeze({
    feefines: {
      type: 'okapi',
      records: 'feefines',
      path: 'feefines',
      throwErrors: false,
      GET: {
        path: `feefines?query=automatic%3D%22true%22%20sortby%20feeFineType&limit=${MAX_RECORDS}`,
      },
      PUT: {
        path: 'feefines/%{activeRecord.id}',
      },
    },
    templates: {
      type: 'okapi',
      records: 'templates',
      path: `templates?limit=${MAX_RECORDS}&query=cql.allRecords=1 AND category=""`,
      accumulate: 'true',
    },
    activeRecord: {},
  });

  static propTypes = {
    stripes: stripesShape.isRequired,
    resources: PropTypes.object,
    mutator: PropTypes.shape({
      feefines: PropTypes.shape({
        PUT: PropTypes.func.isRequired,
      }),
      templates: PropTypes.shape({
        GET: PropTypes.func.isRequired,
      }),
      activeRecord: PropTypes.shape({
        update: PropTypes.func,
      }),
    }).isRequired,
    intl: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { templates: [] };
    this.connectedControlledVocab = props.stripes.connect(ControlledVocab);
  }

  componentDidMount() {
    this.props.mutator.templates.GET().then(records => {
      this.setState({ templates: records });
    });
  }

  getNotice = (noticeTypeId) => {
    const { templates } = this.state;
    if (!noticeTypeId) return <NoValue />;
    const template = templates.find(t => t.id === noticeTypeId);
    return template ? template.name : <NoValue />;
  }

  render() {
    const { intl, stripes } = this.props;
    const { templates } = this.state;
    const hasEditPerm = stripes.hasPerm('feefines.item.put');

    let templateCharge = templates.filter(t => t.category === 'FeeFineCharge');
    templateCharge = [{}, ...templateCharge.map(t => ({ value: t.id, label: t.name }))];

    let templateAction = templates.filter(t => t.category === 'FeeFineAction');
    templateAction = [{}, ...templateAction.map(t => ({ value: t.id, label: t.name }))];

    const fieldComponents = {
      chargeNoticeId: ({ fieldProps }) => (
        <Field
          {...fieldProps}
          marginBottom0
          component={Select}
          dataOptions={templateCharge}
        />
      ),
      actionNoticeId: ({ fieldProps }) => (
        <Field
          {...fieldProps}
          marginBottom0
          component={Select}
          dataOptions={templateAction}
        />
      ),
    };

    const formatter = {
      chargeNoticeId: ({ chargeNoticeId }) => this.getNotice(chargeNoticeId),
      actionNoticeId: ({ actionNoticeId }) => this.getNotice(actionNoticeId),
    };

    const preUpdateHook = (item) => {
      return {
        ...item,
        automatic: true,
      };
    };

    return (
      <this.connectedControlledVocab
        stripes={stripes}
        baseUrl="feefines"
        columnMapping={columnMapping}
        fieldComponents={fieldComponents}
        formatter={formatter}
        hiddenFields={['lastUpdated', 'numberOfObjects']}
        id="settings-automatic-feefines"
        label={intl.formatMessage({ id: 'ui-users.feefines.automatic.title' })}
        labelSingular={intl.formatMessage({ id: 'ui-users.feefines.automatic.singular' })}
        nameKey="feefine"
        objectLabel=""
        preUpdateHook={preUpdateHook}
        records="feefines"
        rowFilterFunction={(item) => item.automatic === true}
        readOnlyFields={['feeFineType']}
        sortby="feeFineType"
        visibleFields={['feeFineType', 'chargeNoticeId', 'actionNoticeId']}
        formType="final-form"
        shouldReinitialize
        canCreate={false}
        actionSuppressor={{
          delete: () => true,
          edit: () => !hasEditPerm,
        }}
      />
    );
  }
}

export default injectIntl(withStripes(stripesConnect(AutomaticFeeFineSettings)));
/* kware end editing */
